import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { STATUSES, STATUS_MAP, StatusBadge } from "@/lib/status";
import { Download, Loader2, ChevronDown } from "lucide-react";

export default function CandidatesPage() {
  const [candidates, setCandidates] = useState([]);
  const [positionsMap, setPositionsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [openMenu, setOpenMenu] = useState(null);

  const fetchAll = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/candidates"), api.get("/positions")]);
      setCandidates(c.data);
      const map = {}; p.data.forEach((pp) => (map[pp.id] = pp));
      setPositionsMap(map);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  // Close menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest("[data-status-menu]")) setOpenMenu(null);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const filtered = candidates.filter((c) => {
    const status = c.status || "submitted";
    if (filter !== "all" && status !== filter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    const pos = positionsMap[c.position_id];
    return [c.name, c.email, c.recruiter_name, pos?.position_name, pos?.client_company]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  // Per-status counts (across ALL candidates, not filtered, so user can see what's hidden)
  const counts = STATUSES.reduce((m, s) => {
    m[s.value] = candidates.filter((c) => (c.status || "submitted") === s.value).length;
    return m;
  }, {});

  const downloadResume = async (cand) => {
    try {
      const res = await api.get(`/candidates/${cand.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = cand.resume_file_name || `${cand.name}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  const changeStatus = async (cand, newStatus) => {
    if (newStatus === (cand.status || "submitted")) { setOpenMenu(null); return; }
    try {
      const { data } = await api.patch(`/candidates/${cand.id}/status`, { status: newStatus });
      setCandidates((prev) => prev.map((c) => (c.id === cand.id ? { ...c, status: data.status, status_history: data.status_history } : c)));
      setOpenMenu(null);
    } catch (e) { alert(formatErr(e)); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader testId="candidates-header" title="Candidates" subtitle="Pipeline of every candidate submitted across all positions." />

      {/* Pipeline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="pipeline-tiles">
        <PipelineTile
          label="All" value={candidates.length} active={filter === "all"} onClick={() => setFilter("all")} colorClass="bg-white"
          testId="pipeline-tile-all"
        />
        {STATUSES.map((s) => (
          <PipelineTile
            key={s.value}
            label={s.label}
            value={counts[s.value] || 0}
            active={filter === s.value}
            onClick={() => setFilter(s.value)}
            colorClass={s.color}
            testId={`pipeline-tile-${s.value}`}
          />
        ))}
      </div>

      <div className="card-base">
        <div className="p-4 border-b border-gray-100">
          <input
            data-testid="candidates-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by candidate, email, recruiter, position or company…"
            className="w-full outline-none text-sm bg-transparent"
          />
        </div>
        {loading ? <div className="p-8 text-gray-500 text-sm flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Loading…</div>
          : filtered.length === 0 ? <div className="p-6"><Empty title="No candidates" subtitle="Try a different filter or search term." /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="candidates-table">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Candidate</Th><Th>Position</Th><Th>Recruiter</Th><Th>Rate</Th><Th>Submitted</Th><Th>Status</Th><Th>Resume</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const pos = positionsMap[c.position_id];
                    const status = c.status || "submitted";
                    return (
                      <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-gray-500">{c.email}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-gray-900">{pos?.position_name || "—"}</div>
                          <div className="text-xs text-gray-500">{pos?.client_company}</div>
                        </td>
                        <td className="py-3 px-4">{c.recruiter_name}</td>
                        <td className="py-3 px-4 font-mono text-xs">{c.rate || "—"}</td>
                        <td className="py-3 px-4 text-xs text-gray-500">{new Date(c.submitted_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4 relative" data-status-menu>
                          <button
                            onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === c.id ? null : c.id); }}
                            className="inline-flex items-center gap-1.5 hover:bg-gray-100 rounded px-1 py-0.5"
                            data-testid={`status-button-${c.id}`}
                          >
                            <StatusBadge status={status} />
                            <ChevronDown size={12} className="text-gray-400" />
                          </button>
                          {openMenu === c.id && (
                            <div className="absolute z-20 mt-1 left-4 bg-white border border-gray-200 rounded-md shadow-xl min-w-[160px] py-1" data-status-menu>
                              {STATUSES.map((s) => (
                                <button
                                  key={s.value}
                                  onClick={() => changeStatus(c, s.value)}
                                  className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2 ${status === s.value ? "bg-gray-50 font-semibold" : ""}`}
                                  data-testid={`set-status-${s.value}-${c.id}`}
                                >
                                  <StatusBadge status={s.value} />
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {c.resume_file_name ? (
                            <button onClick={() => downloadResume(c)} className="text-brand hover:underline text-xs inline-flex items-center gap-1" data-testid={`download-resume-${c.id}`}>
                              <Download size={12} /> Download
                            </button>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

function PipelineTile({ label, value, active, onClick, colorClass, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-3 rounded-md border transition-all ${active ? "ring-2 ring-brand border-brand bg-white shadow" : "border-gray-200 bg-white hover:border-gray-400"}`}
    >
      <div className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${colorClass} mb-1.5`}>
        {label}
      </div>
      <div className="font-mono text-2xl font-bold text-gray-900">{value}</div>
    </button>
  );
}

function Th({ children }) { return <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">{children}</th>; }
